declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    dataLayer: any[];
  }
}

function generateEventId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Meta Catalog Retailer ID is the human SKU (`barcode`, e.g. "390001407").
// Parent-variant docs carry the placeholder "-----" and no real SKU; fall
// back to the Mongo ObjectId (`id` / `_id`) so those events still fire —
// they just won't match the Catalog until a real variant is selected.
export function metaId(
  barcode?: string | null,
  fallbackId?: string | null,
): string {
  if (barcode && barcode !== "-----") return String(barcode);
  return String(fallbackId ?? "");
}

// Fire fbq('track', name, params, {eventID}). Returns the eventID so the
// caller can send the same value to backend CAPI for dedup. Pass `eventId`
// to override the auto-UUID (Purchase uses OrderId for dedup with the
// backend's order-creation CAPI event).
export function trackFbEvent(
  eventName: string,
  params?: Record<string, any>,
  eventId?: string,
): string {
  const id = eventId ?? generateEventId();
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("track", eventName, params, { eventID: id });
  }
  return id;
}

// Resolve the same /api/... proxy prefix usePost builds, so /meta/track
// reaches the backend through the same Vercel rewrite that handles auth
// cookies.
function resolveMetaTrackURL(): string {
  let baseURL = process.env.NEXT_PUBLIC_CLIENT_BASE_URL;
  if (!baseURL && process.env.NEXT_PUBLIC_BASE_URL) {
    try {
      const parsed = new URL(process.env.NEXT_PUBLIC_BASE_URL);
      baseURL = "/api" + parsed.pathname;
    } catch {
      baseURL = process.env.NEXT_PUBLIC_BASE_URL;
    }
  }
  baseURL = baseURL || process.env.NEXT_PUBLIC_BASE_URL || "/";
  if (!baseURL.endsWith("/")) baseURL += "/";
  return `${baseURL}meta/track`;
}

// Fire-and-forget POST to backend CAPI relay. Used for PageView /
// ViewContent / AddToCart / InitiateCheckout. NOT for Purchase — backend
// fires that CAPI event at order creation.
export function postMetaTrack(
  eventName: string,
  eventId: string,
  customData?: Record<string, any>,
): void {
  if (typeof window === "undefined") return;
  try {
    fetch(resolveMetaTrackURL(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      keepalive: true,
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: window.location.href,
        custom_data: customData ?? {},
      }),
    }).catch(() => {});
  } catch {
    // swallow — dedup miss is tolerable, never block the page
  }
}

// Fire fbq + post /meta/track with the same event_id. Do not use for
// Purchase.
export function trackFbAndCAPI(
  eventName: string,
  params?: Record<string, any>,
): string {
  const id = trackFbEvent(eventName, params);
  postMetaTrack(eventName, id, params);
  return id;
}

// Push a GA4 event to the GTM dataLayer.
// Example: pushDataLayer("add_to_cart", { ecommerce: { currency: "KWD", value: 25.99, items: [...] } })
export function pushDataLayer(event: string, data: Record<string, any> = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // Clear ecommerce object before each push (GA4 best practice)
  if (data.ecommerce) {
    window.dataLayer.push({ ecommerce: null });
  }
  window.dataLayer.push({ event, ...data });
}
